/**
 * The default capability priority (optum-agent Feature 87, D11, P2): the
 * level every capability without its own takes. The claim loop reads levels
 * each round, so a change applies without a restart. A platform admin sets
 * it; anyone else reads it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import {
  getPriorityDefault,
  setPriorityDefault,
} from '@optum/api-client/endpoints/capability-priorities';
import type { CapabilityLevel } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Field } from '../../ui/Field.js';
import { Select } from '../../ui/Select.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { LEVEL_LABEL, LEVEL_OPTIONS, isLevel } from '../../lib/priority.js';

export function PriorityDefault() {
  const { isPlatformAdmin } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  const current = useQuery({ queryKey: qk.priorityDefault(), queryFn: getPriorityDefault });

  const save = useMutation({
    mutationFn: (level: CapabilityLevel) => setPriorityDefault(level),
    onSuccess: (next) => {
      toast.success(`Default priority is ${LEVEL_LABEL[next.level]}`);
      qc.setQueryData(qk.priorityDefault(), next);
      // Every capability on its default now reads the new level.
      void qc.invalidateQueries({ queryKey: qk.capabilityPriorities() });
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  if (current.isPending) return <LoadingBlock />;
  if (current.isError) {
    return (
      <ErrorBlock
        message={current.error instanceof Error ? current.error.message : 'Failed to load'}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        Inside one organization, waiting tasks are claimed by level: the top waiting level takes at
        most half of the claims, the next half of the rest, and so on. Organizations still take
        turns, so a level never takes a slot from another organization. Each capability's own level
        is set on its plugin page.
      </p>
      <Field
        id="set-priority-default"
        label="Default for capabilities without their own level"
        hint="Applies at the next claim round; no restart."
      >
        <Select
          id="set-priority-default"
          options={LEVEL_OPTIONS}
          value={current.data.level}
          onChange={(value) => {
            if (isLevel(value) && value !== current.data.level) save.mutate(value);
          }}
          disabled={!isPlatformAdmin || save.isPending}
          width={220}
        />
      </Field>
    </div>
  );
}
